import {
  symbolsJson,
  constantsJson,
  decoratorsJson,
  functionsJson,
  operatorSymbolsJson,
  operatorsJson,
} from "@corca/data";
import { toJS } from "mobx";
import * as Y from "yjs";

import { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { DecoratorNodeModel } from "@/models/nodes/DecoratorNodeModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { prettyPrintNodes } from "@/utils/debug/prettyPrintNodes";

import { createLogger } from "./logger";

import type { DocumentModel, YNode } from "@/models/DocumentModel";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { SerializedNode, NodeId, NodePropsValue } from "@/models/nodes/types";

const logger = createLogger("migrateDocVersion");

const SCHEMA_VERSION = 4;

export class InvalidSchemaVersionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidSchemaVersionError";
  }
}

export function migrateDocVersion(doc: DocumentModel) {
  if (!doc.yDoc) {
    return;
  }

  const meta = doc.yDoc.getMap("docMeta");
  const docSchemaVersion = (meta.get("schemaVersion") as number) ?? 0;

  if (docSchemaVersion === SCHEMA_VERSION) return;

  if (docSchemaVersion > SCHEMA_VERSION) {
    throw new InvalidSchemaVersionError(
      `Document version (v${docSchemaVersion}) does not match client version (v${SCHEMA_VERSION})`,
    );
    return;
  }

  if (docSchemaVersion < 1) {
    fixOperatorSymbolValue(doc);
    replaceCustomFunctionNodesWithVariables(doc);
    replaceGroupChildInlineNodeWithMatrixNode(doc);
    migrateLatexExports(doc);
  }

  if (docSchemaVersion < 2) {
    migrateNodeObjectToYMap(doc);
  }

  if (docSchemaVersion < 3) {
    migrateBlockExpressions(doc);
  }

  if (docSchemaVersion < 4) {
    migrateFunctionOmitParentheses(doc);
  }

  meta.set("schemaVersion", SCHEMA_VERSION);
}

/**
 * Fixes missing symbolNode for OperatorSymbolNodeModel after https://github.com/corca-dev/corca-app/pull/504
 * In the previous version of OperatorSymbolNodeModel, we used props.value to store the symbol (e.g., "+").
 * In the new version, we've added a symbolNode property, which is a SymbolNodeModel, to enable adding
 * indexes for operator symbols.
 * OperatorSymbolNodeModel.props.value => OperatorSymbolNodeModel.symbolNode.value
 */
function fixOperatorSymbolValue(doc: DocumentModel) {
  doc.nodes.forEach((node) => {
    // @ts-expect-error previous version of OperatorSymbolNodeModel had props.value
    if (node.is(OperatorSymbolNodeModel) && !node.symbolNode && node.props.value) {
      // @ts-expect-error previous version of OperatorSymbolNodeModel had props.value
      const value = node.props.value as string;
      node.appendChild(doc.create(SymbolNodeModel, value));
      // @ts-expect-error previous version of OperatorSymbolNodeModel had props.value
      const { value: _drop, ...rest } = node.props;
      node.setProps(rest);
    }
  });
}

/**
 * Converts legacy custom (not built-in) constants to variables with parameters
 */
function replaceCustomFunctionNodesWithVariables(doc: DocumentModel) {
  doc.nodes.forEach((node) => {
    if (node.is(FunctionNodeModel) && !node.isBuiltIn && node.symbolNode) {
      const symbolNode = createNodeFrom(node.symbolNode.toJSON(), doc, {
        resetIds: true,
      }) as SymbolNodeModel;
      const parameters = node.parameters.map(
        (parameter) =>
          createNodeFrom(parameter.toJSON(), doc, {
            resetIds: true,
          }) as InlineNodeModel,
      );
      const variable = doc.create(
        VariableNodeModel,
        symbolNode,
        {
          name: node.name,
          color: node.color,
        },
        parameters,
      );

      node.replaceWith(variable);
    }
  });
}

/**
 * Replace group's child inline node with 1:1 matrix.
 */
function replaceGroupChildInlineNodeWithMatrixNode(doc: DocumentModel) {
  doc.nodes.forEach((node) => {
    if (node.is(GroupNodeModel) && !node.matrixNode) {
      const matrix = doc.create(
        MatrixNodeModel,
        { rows: 1, cols: 1 },
        node.nodes as InlineNodeModel[],
      );
      node.setChildren([matrix]);
    }
  });
}

function migrateLatexExports(doc: DocumentModel) {
  doc.nodes.forEach((node) => {
    if (node.is(FunctionNodeModel) && node.isBuiltIn && !node.props.key) {
      const func = functionsJson.find((f) => f.name === node.props.name && f.symbol === node.value);
      if (func) {
        node.updateProps({ key: func.key });
        if (node.symbolNode) {
          logger.log("migrate function", node.value);
          node.symbolNode.updateProps({ key: func.symbolProps?.key });
        }
      }
    }

    if (node.is(ConstantNodeModel) && !node.symbolNode?.props.key) {
      if (node.symbolNode) {
        const constant = constantsJson.find(
          (c) => c.name === node.props.name && c.symbol === node.symbolNode?.value,
        );
        if (constant) {
          logger.log("migrate constant symbol", toJS(node.symbolNode?.props));
          node.symbolNode.updateProps({ key: constant.symbolProps?.key });
        }
      }
    }

    if (node.is(DecoratorNodeModel) && !node.props.key) {
      const decorator = decoratorsJson[node.props.name];
      if (decorator) {
        logger.log("migrate decorator", toJS(node.props));
        node.updateProps({ key: decorator.key });
      }
    }

    if (node.is(OperatorSymbolNodeModel) && !node.symbolNode?.props.key) {
      if (node.symbolNode) {
        const opSymbol = operatorSymbolsJson.find(
          (o) => o.name === node.props.name && o.symbol === node.symbolNode?.value,
        );
        if (opSymbol) {
          logger.log("migrate operator symbol", node.props.name, opSymbol);
          node.symbolNode.updateProps({ key: opSymbol.key });
        }
      }
    }

    if (node.is(OperatorNodeModel) && !node.props.key) {
      const op = operatorsJson[node.props.operatorType];
      if (op) {
        logger.log("migrate operator", node.props.name, op);
        node.updateProps({ key: op.key });
      }
    }

    if (node.is(VariableNodeModel) && !node.symbolNode?.props.key) {
      if (node.symbolNode) {
        const symbol = functionsJson.find(
          (s) => s.name === node.props.name && s.symbol === node.symbolNode?.props.value,
        );
        if (symbol) {
          logger.log("migrate variable symbol", node.props.name, symbol);
          node.symbolNode.updateProps({ key: symbol.symbolProps?.key });
        }
      }
    }
  });

  // migrate the rest of variable symbols
  doc.nodes.forEach((node) => {
    if (node.is(SymbolNodeModel) && !node.props.key) {
      const symbol = symbolsJson.find((s) => s.symbol === node.props.value);
      if (symbol) {
        logger.log("migrate symbol", node.props.value, symbol);
        node.updateProps({ key: symbol.key });
      }
    }
  });
}

function migrateNodeObjectToYMap(doc: DocumentModel) {
  if (!doc.yDoc) {
    return;
  }

  const yMapV1 = doc.yDoc.getMap<SerializedNode>("nodesV1");
  const yMap = doc.yDoc.getMap<SerializedNode>("nodes");

  const logs: string[] = [];

  yMap.forEach((node) => {
    if (!node.id) {
      return;
    }
    logs.push(`migrated: ${prettyPrintNodes([node])}`);

    // Temporary saving previous version of nodes as a backup.
    // We will need to remove it if everything works fine.
    yMapV1.set(node.id, node);

    const { id, type, props = {}, childrenIds = [] } = node;

    const yNodeMap = new Y.Map<YNode[keyof YNode]>();
    yNodeMap.set("id", id);
    yNodeMap.set("type", type);

    const yPropsMap = new Y.Map<NodePropsValue>();
    yNodeMap.set("props", yPropsMap);

    if (props) {
      Object.entries(props).forEach(([key, value]) => {
        yPropsMap.set(key, value);
      });
    }

    const childrenArray = new Y.Array<NodeId>();
    childrenArray.insert(0, childrenIds);
    yNodeMap.set("childrenIds", childrenArray);

    // @ts-expect-error we replace the old value with the new one with the different type
    yMap.set(id, yNodeMap);
  });

  if (logger.enabled("DEBUG")) {
    logger.debug("Migrate nodeObjectToYMap", `\n${logs.join("\n")}`);
  }
}

function migrateBlockExpressions(doc: DocumentModel) {
  if (!doc.yDoc || !doc.yDoc.share.get("textNode")) return;

  function replaceBlockExpressions(node: Y.XmlFragment | Y.XmlText | Y.XmlHook) {
    if (node instanceof Y.XmlElement || node instanceof Y.XmlFragment) {
      const children = Array.from(node.toArray());

      for (let i = 0; i < children.length; i++) {
        const child = children[i];

        replaceBlockExpressions(child);

        if (child instanceof Y.XmlElement && child.nodeName === "block-expression") {
          const inlineExpr = new Y.XmlElement("inline-expression");

          for (const [key, value] of Object.entries(child.getAttributes())) {
            if (value !== undefined) inlineExpr.setAttribute(key, value);
          }

          const pElement = new Y.XmlElement("p");
          pElement.push([inlineExpr]);

          node.delete(i, 1);
          node.insert(i, [pElement]);
        }
      }
    }
  }

  const textNode = doc.yDoc.getXmlFragment("textNode");

  doc.yDoc.transact(() => {
    replaceBlockExpressions(textNode);
  });
}

function migrateFunctionOmitParentheses(doc: DocumentModel) {
  doc.nodes.forEach((node) => {
    if (node.is(FunctionNodeModel) && node.isBuiltIn) {
      // @ts-expect-error migrating old prop
      const { omitParentheses } = node.props;

      if (typeof omitParentheses === "boolean") {
        // @ts-expect-error migrating old prop
        const { omitParentheses: _drop, ...rest } = node.props;
        node.setProps({
          ...rest,
          parenthesesVisibility: omitParentheses ? "auto" : "visible",
        });
      }
    }
  });
}
